import { useMemo } from 'react'
import { NumberParam, StringParam, useQueryParams } from 'use-query-params'

import { getMyCampaigns } from '@percent/workplace-giving/api/campaigns/getMyCampaigns/getMyCampaigns'
import { useQueryList } from '@percent/workplace-giving/common/hooks/useQueryList/useQueryList'
import { GivingCampaignStatusDto } from '@percent/workplace-giving/api/campaigns/campaign.types'
import { usePermission } from '@percent/workplace-giving/common/hooks/usePermission/usePermission'
import { scope } from '@percent/workplace-giving/common/hoc/withPermissions'

export const useMyCampaignsQuery = () => {
  const has = usePermission()
  const givingCampaignEnabled = has(scope('giving_campaign'))

  const [rawQuery, setQuery] = useQueryParams({
    pageSize: NumberParam,
    direction: StringParam,
    status: StringParam,
    startDateGte: StringParam,
    startDateLte: StringParam,
    endDateGte: StringParam,
    endDateLte: StringParam,
    name: StringParam,
    cursor: StringParam
  })

  const query = useMemo(
    () => ({
      pageSize: rawQuery.pageSize ?? 25,
      direction: ['ASC', 'DESC'].includes(rawQuery.direction ?? 'ASC')
        ? (rawQuery.direction as 'ASC' | 'DESC')
        : undefined,
      status: [
        GivingCampaignStatusDto.DRAFT,
        GivingCampaignStatusDto.PUBLISHED,
        GivingCampaignStatusDto.CANCELLED
      ].includes(rawQuery.status as GivingCampaignStatusDto)
        ? (rawQuery.status as GivingCampaignStatusDto)
        : undefined,
      startDateGte: rawQuery.startDateGte ?? undefined,
      startDateLte: rawQuery.startDateLte ?? undefined,
      endDateGte: rawQuery.endDateGte ?? undefined,
      endDateLte: rawQuery.endDateLte ?? undefined,
      name: rawQuery.name ?? undefined,
      cursor: rawQuery.cursor ?? undefined
    }),
    [rawQuery]
  )

  const {
    data,
    isLoading,
    isError,
    isRefetching,
    refetch,
    fetchNextPage,
    fetchPreviousPage,
    hasNextPage,
    hasPreviousPage
  } = useQueryList('getMyCampaigns', query, getMyCampaigns, setQuery, {
    enabled: givingCampaignEnabled
  })

  return {
    data,
    isLoading,
    isError,
    isRefetching,
    refetch,
    fetchNextPage,
    fetchPreviousPage,
    hasNextPage,
    hasPreviousPage
  }
}
